import * as XLSX from 'xlsx';

export interface ExportColumn {
  field: string;
  header: string;
  width?: number;
  /** Compute a value from the full row instead of just row[field]. */
  valueGetter?: (row: Record<string, any>) => any;
}

/** Resolve the currency code for a given field from the row data or header text. */
export function resolveCurrency(field: string, row: Record<string, any>, header: string): string {
  // field_currency  (e.g. unit_cost → unit_cost_currency)
  if (row[field + '_currency']) return row[field + '_currency'];
  // prefix_currency (e.g. spot_price → spot_currency, contract_price → contract_currency)
  const prefix = field.replace(/_(?:price|value|cost|total)$/i, '');
  if (prefix !== field && row[prefix + '_currency']) return row[prefix + '_currency'];
  // Extract from header like "Purchase Value (USD)"
  const match = header.match(/\(([A-Z]{3})\)/);
  if (match) return match[1];
  return '';
}

/**
 * Export rows to an Excel file and trigger download.
 * Numbers stay as numbers, Dates become native Excel dates.
 */
export function exportToExcel(
  rows: Record<string, any>[],
  columns: ExportColumn[],
  filename: string,
) {
  const headers = columns.map(c => c.header);
  const data = rows.map(row =>
    columns.map(c => {
      const v = c.valueGetter ? c.valueGetter(row) : row[c.field];
      if (v === null || v === undefined) return '';
      return v;
    }),
  );

  const ws = XLSX.utils.aoa_to_sheet([headers, ...data], { cellDates: true });

  // Apply date format to Date cells
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  for (let R = range.s.r + 1; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell && cell.v instanceof Date) {
        cell.t = 'd';
        cell.z = 'YYYY-MM-DD';
      }
    }
  }

  // Column widths
  ws['!cols'] = columns.map(c => ({ wch: c.width || 16 }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Data');
  XLSX.writeFile(wb, `${filename}.xlsx`);
}
